import { memo, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { clsx } from 'clsx';
import { SearchField } from '@library';
import CollapsibleContent from '../../../core/components/CollapsibleContent';
import CollapsibleSideBarFilterTitle from '../ProductList/Filters/CollapsibleSideBarFilterTitle';
import styles from './Filter.module.scss';
import FilterShowMore from './FilterShowMore';

export const FiltersSearchField = memo(
    ({
        placeholder,
        setSearch,
        onFocus,
    }: {
        placeholder: string;
        setSearch: (value: string) => void;
        onFocus?: () => void;
    }) => {
        const [value, setValue] = useState('');

        const handleChange = (eventValue: string) => {
            setValue(eventValue);
            setSearch(eventValue);
        };

        return (
            <div className={styles.search}>
                <SearchField
                    light
                    small
                    aria-label="search"
                    value={value}
                    onChange={handleChange}
                    placeholder={placeholder}
                    onFocus={onFocus}
                />
            </div>
        );
    }
);

const FilterWrapper = ({
    name,
    items,
    setSearch,
    defaultExpand = true,
}: {
    name: string;
    items: {
        render(
            setShowFullList: React.Dispatch<React.SetStateAction<boolean>>
        ): ReactNode;
        isSelected?: boolean;
    }[];
    setSearch?: React.Dispatch<React.SetStateAction<string>>;
    defaultExpand?: boolean;
}) => {
    const [expand, setExpand] = useState(defaultExpand);

    const expanded = (isExpanded: boolean) => {
        setExpand(isExpanded);
    };

    useEffect(() => {
        setExpand(defaultExpand);
    }, [defaultExpand]);

    return (
        <CollapsibleContent
            onExpand={expanded}
            expanded={expand}
            smallIcon
            filterIcon
            title={
                <CollapsibleSideBarFilterTitle>
                    {name}
                </CollapsibleSideBarFilterTitle>
            }
            endArrow
        >
            {expand && <FilterContent items={items} setSearch={setSearch} />}
        </CollapsibleContent>
    );
};

const FilterContent = memo(
    ({
        items,
        setSearch,
    }: {
        items: {
            render(
                setShowFullList: React.Dispatch<React.SetStateAction<boolean>>
            ): ReactNode;
            isSelected?: boolean;
        }[];
        setSearch?: React.Dispatch<React.SetStateAction<string>>;
    }) => {
        const intl = useIntl();
        const [showFullList, setShowFullList] = useState(false);

        const selectedItems = items.filter(({ isSelected }) => isSelected);
        const nonSelectedItems = items.filter(({ isSelected }) => !isSelected);

        let itemsListEnd: number | undefined;
        if (!showFullList) {
            if (selectedItems.length < 5) {
                itemsListEnd = 5 - selectedItems.length;
            } else {
                itemsListEnd = 1;
            }
        }

        const renderNonSelectedItems = () =>
            nonSelectedItems
                .slice(0, itemsListEnd)
                .map(item => item.render(setShowFullList));

        const remainingItems =
            items.length -
            selectedItems.length -
            Math.max(5 - selectedItems.length, 1);

        return (
            <>
                {setSearch && (
                    <div className={styles.searchField}>
                        <FiltersSearchField
                            setSearch={setSearch}
                            placeholder={`${intl.formatMessage({
                                id: 'search_verb',
                            })}`}
                        />
                    </div>
                )}
                <div
                    className={clsx({
                        [styles.values]: items.length > 5,
                    })}
                >
                    {selectedItems.map(item => item.render(setShowFullList))}
                    {renderNonSelectedItems()}
                    {remainingItems > 0 && (
                        <FilterShowMore
                            showFullList={showFullList}
                            setShowFullList={setShowFullList}
                            remainingItems={remainingItems}
                        />
                    )}
                </div>
            </>
        );
    }
);

export default memo(FilterWrapper);
